"use client"

import React, { Suspense, useEffect, useState } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import AuthBgWrapper from '@/features/auth/components/auth-bg-wrapper'
import AuthCardWrapper from '@/features/auth/components/auth-card-wrapper'
import SignupForm from '@/features/auth/components/forms/sign-up-form/sign-up-form'
import SignupFormProvider from '@/features/auth/components/forms/sign-up-form/signup-form-provider'
import { sigUpScreenBgImage } from '@/features/auth/constants/image'
import { SignupFormStepsProvider, useSignupFormStepsContext } from '@/features/auth/context/signup-form-steps-context'
import { SignupUserIdContextProvider } from '@/features/auth/context/signup-user-id-context'
import { useFormContext } from 'react-hook-form'
import { SignupFormType } from '@/features/auth/types/signup.type'
import { Button } from '@/shared/components/ui/button'
import { AUTH_PAGE_ROUTE } from '@/shared/constants/pages-routes'

// Component to set form values after mount to avoid hydration mismatch
// Reads from sessionStorage instead of URL params for security
function FormValueSetter() {
    const { setValue, trigger } = useFormContext<SignupFormType>()
    const [hasSetValues, setHasSetValues] = useState(false)
    
    useEffect(() => {
        // Only run once
        if (hasSetValues) return
        
        // Read email and password from sessionStorage (set by login form when email not found)
        const email = sessionStorage.getItem('registrationEmail')
        const password = sessionStorage.getItem('registrationPassword')
        
        if (!email && !password) {
            setHasSetValues(true)
            return
        }
        
        // Set values after form is fully initialized
        const setValues = async () => {
            try {
                if (email) {
                    setValue('form1.email', email, { 
                        shouldValidate: false,
                        shouldDirty: true,
                        shouldTouch: true 
                    })
                }
                if (password) {
                    // Only autofill the password field, not confirmPassword
                    // User must manually confirm the password
                    setValue('form1.password', password, { 
                        shouldValidate: false,
                        shouldDirty: true,
                        shouldTouch: true 
                    })
                }
                
                // Trigger re-render of form fields
                await trigger(['form1.email', 'form1.password'])
                
                // Mark as set and clear from sessionStorage
                setHasSetValues(true)
                if (email) sessionStorage.removeItem('registrationEmail')
                if (password) sessionStorage.removeItem('registrationPassword')
            } catch (error) {
                console.error('Error setting form values from sessionStorage:', error)
            }
        }
        
        // Try after a delay to ensure form fields are registered
        const timer = setTimeout(() => {
            requestAnimationFrame(setValues)
        }, 200)
        
        return () => clearTimeout(timer)
    }, [setValue, trigger, hasSetValues])
    
    return null
}

/** Renders the card with context-aware header (OTP step vs. details step) */
function EmailRegistrationCard({
    selectedRole,
    showRoleButtons,
    onRoleSelected,
    defaultValues,
}: {
    selectedRole: 'volunteer' | 'homeowner' | null
    showRoleButtons: boolean
    onRoleSelected: (role: 'volunteer' | 'homeowner') => void
    defaultValues: Partial<SignupFormType>
}) {
    const { registrationOtpSent } = useSignupFormStepsContext()
    const headerDescription = registrationOtpSent
        ? 'We sent a 6-digit code to your email. Enter it below to complete signup.'
        : 'We need a few more details to finish creating your account.'
    return (
        <AuthCardWrapper
            headerTitle='Create Your Account'
            headerDescription={headerDescription}
        >
            <SignupFormProvider defaultValues={defaultValues}>
                <SignupUserIdContextProvider>
                    <FormValueSetter />
                    <SignupForm selectedRole={selectedRole} showRoleButtons={showRoleButtons} onRoleSelected={onRoleSelected} />
                </SignupUserIdContextProvider>
            </SignupFormProvider>
        </AuthCardWrapper>
    )
}

function EmailRegistrationContent() {
    const searchParams = useSearchParams()
    const router = useRouter()
    const [selectedRole, setSelectedRole] = useState<'volunteer' | 'homeowner' | null>(null)
    
    // Check for role in URL params first, then sessionStorage
    useEffect(() => {
        // Check URL params first (highest priority)
        const urlRole = searchParams.get('role')
        
        // If role exists in URL but is invalid, redirect to /role page
        if (urlRole && urlRole !== 'volunteer' && urlRole !== 'homeowner') {
            console.warn('[EmailRegistration] Invalid role in URL:', urlRole, '- redirecting to /role')
            router.push('/role')
            return
        }
        
        if (urlRole === 'volunteer' || urlRole === 'homeowner') {
            setSelectedRole(urlRole as 'volunteer' | 'homeowner')
            sessionStorage.setItem('registrationRole', urlRole)
            // Also sync to unifiedAuthRole for consistency
            sessionStorage.setItem('unifiedAuthRole', urlRole)
            return
        }
        
        // Check sessionStorage for role - check both registrationRole and unifiedAuthRole
        const registrationEmail = sessionStorage.getItem('registrationEmail')
        const role = sessionStorage.getItem('registrationRole') || sessionStorage.getItem('unifiedAuthRole')
        
        if (registrationEmail && (role === 'volunteer' || role === 'homeowner')) {
            setSelectedRole(role)
            // Sync to both keys for consistency
            sessionStorage.setItem('registrationRole', role)
            sessionStorage.setItem('unifiedAuthRole', role)
        } else if (role === 'volunteer' || role === 'homeowner') {
            // Even without registrationEmail, if we have a role from URL params, use it
            setSelectedRole(role)
            sessionStorage.setItem('registrationRole', role)
            sessionStorage.setItem('unifiedAuthRole', role)
        } else {
            // Clear stale role data if no active registration flow
            if (!registrationEmail) {
                sessionStorage.removeItem('registrationRole')
            }
        }
    }, [searchParams, router])
    
    const handleRoleSelected = (role: 'volunteer' | 'homeowner') => {
        sessionStorage.setItem('registrationRole', role)
        setSelectedRole(role)
    }
    
    // Determine if we should show role selection buttons
    // Show buttons if no role is specified (neither in URL nor sessionStorage)
    const showRoleButtons = !selectedRole
    
    // Always use empty default values to ensure server/client match
    const defaultValues: Partial<SignupFormType> = {
        form1: {
            fullname: "",
            phone: "",
            password: "",
            confirmPassword: "",
            email: "",
        }
    }
    
    // Always show the registration form (like OAuth flow)
    return (
        <AuthBgWrapper image={sigUpScreenBgImage}>
            <SignupFormStepsProvider>
                <EmailRegistrationCard
                    selectedRole={selectedRole}
                    showRoleButtons={showRoleButtons}
                    onRoleSelected={handleRoleSelected}
                    defaultValues={defaultValues}
                />
            </SignupFormStepsProvider>
        </AuthBgWrapper>
    )
}

function EmailRegistrationPage() {
    return (
        <Suspense fallback={
            <AuthBgWrapper image={sigUpScreenBgImage}>
                <AuthCardWrapper
                    headerTitle='Create Volunteer Account'
                    headerDescription='Thank you for your yes to bring hope!'
                >
                    <div className="flex items-center justify-center min-h-[200px]">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                            <p className="text-muted-foreground">Loading...</p>
                        </div>
                    </div>
                </AuthCardWrapper>
            </AuthBgWrapper>
        }>
            <EmailRegistrationContent />
        </Suspense>
    )
}

export default EmailRegistrationPage

